import React, { useState, useEffect, useRef } from "react";
import { db, uploadFile } from "@/api/db";
import { Plus, Edit, Trash2, Radio, Calendar, Video, Link2, Upload, Tag, User, Users, Send } from "lucide-react";
import { workshops } from "@/api/db";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { STATUS_CONFIG, CATEGORIES, CATEGORY_COLORS } from "@/lib/live-constants";

const STATUS_TABS = [
  { key: "all",       label: "Todas" },
  { key: "upcoming",  label: "Em breve" },
  { key: "live",      label: "Ao vivo" },
  { key: "recording", label: "Gravação" },
];

function toLocalDatetimeValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY_FORM = {
  title: "",
  description: "",
  category: "",
  live_status: "upcoming",
  live_url: "",
  video_url: "",
  live_date: "",
  image_url: "",
  presenter: "",
  is_published: false,
  max_participants: "",
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminLiveLessons() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bookingCounts, setBookingCounts] = useState({});
  const [sendingReminder, setSendingReminder] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const data = await db.entities.Lesson.filter({ type: "live" }, "-live_date");
    setLessons(data);
    // Load booking counts for oficinas
    const counts = {};
    await Promise.all(
      data.filter(l => l.category === "Oficina").map(async (l) => {
        try {
          const participants = await workshops.participants(l.id);
          counts[l.id] = participants.filter(p => p.status === "confirmed").length;
        } catch { /* ignore */ }
      })
    );
    setBookingCounts(counts);
    setLoading(false);
  }

  async function handleSendReminder(lesson) {
    if (!confirm(`Enviar lembrete para todos os inscritos em "${lesson.title}"?`)) return;
    setSendingReminder(lesson.id);
    try {
      const result = await workshops.sendReminder(lesson.id);
      alert(`Lembrete enviado para ${result.sent} participante(s).`);
    } catch (err) {
      alert("Erro ao enviar lembrete: " + (err.message || "erro desconhecido"));
    }
    setSendingReminder(null);
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(lesson) {
    setEditing(lesson);
    setForm({
      title:        lesson.title        || "",
      description:  lesson.description  || "",
      category:     lesson.category     || "",
      live_status:  lesson.live_status  || "upcoming",
      live_url:     lesson.live_url     || "",
      video_url:    lesson.video_url    || "",
      live_date:    toLocalDatetimeValue(lesson.live_date),
      image_url:    lesson.image_url    || "",
      presenter:    lesson.presenter    || "",
      is_published: !!lesson.is_published,
      max_participants: lesson.max_participants || "",
    });
    setShowForm(true);
  }

  async function handleImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const publicUrl = await uploadFile(file, "lesson-images");
      set("image_url", publicUrl);
    } catch (err) {
      console.error(err);
      alert("Erro ao fazer upload: " + err.message);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleSave() {
    if (!form.title) return;
    setSaving(true);
    const data = {
      ...form,
      type: "live",
      live_date: form.live_date ? new Date(form.live_date).toISOString() : null,
      max_participants: form.max_participants ? parseInt(form.max_participants) : null,
    };
    if (editing) {
      await db.entities.Lesson.update(editing.id, data);
    } else {
      await db.entities.Lesson.create(data);
    }
    await loadData();
    setShowForm(false);
    setSaving(false);
  }

  async function handleDelete(lesson) {
    if (!window.confirm(`Excluir "${lesson.title}"?`)) return;
    await db.entities.Lesson.delete(lesson.id);
    await loadData();
  }

  const filtered = lessons.filter(l =>
    activeTab === "all" || (l.live_status || "upcoming") === activeTab
  );

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Encontros ao Vivo
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "#99A6AB" }}>
            Gerencie os encontros, players e gravações disponíveis na plataforma.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold hover:opacity-90 transition-opacity"
          style={{ background: "#00DCA4", color: "#003326" }}
        >
          <Plus className="w-4 h-4" /> Novo Encontro
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {STATUS_TABS.map(tab => {
          const active = activeTab === tab.key;
          const cfg = tab.key !== "all" ? STATUS_CONFIG[tab.key] : null;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
              style={active
                ? { background: cfg?.bg || "#2E3233", color: cfg?.text || "#fff" }
                : { background: "#F2F4F4", color: "#646C6F" }}
            >
              {tab.key === "live" && active && (
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: cfg?.dot }} />
              )}
              {tab.label}
              <span className="ml-1 opacity-60">
                ({tab.key === "all"
                  ? lessons.length
                  : lessons.filter(l => (l.live_status || "upcoming") === tab.key).length})
              </span>
            </button>
          );
        })}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <Radio className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: "#00664C" }} />
            <p className="font-medium" style={{ color: "#99A6AB" }}>Nenhuma aula encontrada.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(lesson => {
            const statusKey = lesson.live_status || "upcoming";
            const sc = STATUS_CONFIG[statusKey];
            return (
              <Card key={lesson.id} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-4">

                  {/* Thumbnail ou ícone */}
                  {lesson.image_url ? (
                    <img
                      src={lesson.image_url}
                      alt={lesson.title}
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: sc.bg }}
                    >
                      <Radio className="w-6 h-6" style={{ color: sc.text }} />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="font-semibold text-sm" style={{ color: "#2E3233" }}>
                        {lesson.title}
                      </p>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: sc.bg, color: sc.text }}
                      >
                        {statusKey === "live" && (
                          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sc.text }} />
                        )}
                        {sc.label}
                      </span>
                      {lesson.category && CATEGORY_COLORS[lesson.category] && (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ background: CATEGORY_COLORS[lesson.category].bg, color: CATEGORY_COLORS[lesson.category].text }}
                        >
                          {lesson.category}
                        </span>
                      )}
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={lesson.is_published
                          ? { background: "#B0FCEA", color: "#003326" }
                          : { background: "#F2F4F4", color: "#99A6AB" }}
                      >
                        {lesson.is_published ? "Publicada" : "Rascunho"}
                      </span>
                    </div>

                    {lesson.presenter && (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" style={{ color: "#99A6AB" }} />
                        <span className="text-xs" style={{ color: "#99A6AB" }}>{lesson.presenter}</span>
                      </div>
                    )}

                    {lesson.live_date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" style={{ color: "#99A6AB" }} />
                        <span className="text-xs" style={{ color: "#99A6AB" }}>{formatDate(lesson.live_date)}</span>
                      </div>
                    )}

                    {lesson.category === "Oficina" && bookingCounts[lesson.id] !== undefined && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <Users className="w-3 h-3" style={{ color: "#00664C" }} />
                        <span className="text-xs font-medium" style={{ color: "#00664C" }}>
                          {bookingCounts[lesson.id]}{lesson.max_participants ? `/${lesson.max_participants}` : ""} inscritos
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {lesson.category === "Oficina" && (
                        <button
                          onClick={() => handleSendReminder(lesson)}
                          disabled={sendingReminder === lesson.id}
                          className="flex items-center gap-1 text-xs font-medium hover:opacity-80"
                          style={{ color: "#00664C" }}
                        >
                          <Send className="w-3 h-3" />
                          {sendingReminder === lesson.id ? "Enviando..." : "Enviar lembrete"}
                        </button>
                      )}
                      {lesson.live_url && (
                        <a
                          href={lesson.live_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs font-medium hover:opacity-80"
                          style={{ color: "#00664C" }}
                        >
                          <Link2 className="w-3 h-3" /> Player
                        </a>
                      )}
                      {lesson.video_url && (
                        <a
                          href={lesson.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs font-medium hover:opacity-80"
                          style={{ color: "#646C6F" }}
                        >
                          <Video className="w-3 h-3" /> Gravação
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEdit(lesson)}
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ color: "#99A6AB" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#F2F4F4"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(lesson)}
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ color: "#EF9A9E" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#FADBDF"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
              {editing ? "Editar Encontro" : "Novo Encontro"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-1">

            {/* Upload de imagem */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>
                Imagem de capa
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              {form.image_url ? (
                <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
                  <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                  <div
                    className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                    style={{ background: "rgba(0,0,0,0.45)" }}
                  >
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white"
                      style={{ background: "rgba(255,255,255,0.2)" }}
                    >
                      <Upload className="w-4 h-4" /> Trocar imagem
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => set("image_url", "")}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: "rgba(0,0,0,0.5)", color: "#fff" }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl transition-colors"
                  style={{ border: "2px dashed #E5E9EA", background: "#F2F4F4", color: "#99A6AB" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#00DCA4"; e.currentTarget.style.background = "#EAFAF6"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E9EA"; e.currentTarget.style.background = "#F2F4F4"; }}
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
                      <span className="text-sm">Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span className="text-sm font-medium">Clique para fazer upload</span>
                      <span className="text-xs">JPG, PNG ou WebP</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Título */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>Título *</label>
              <Input
                value={form.title}
                onChange={e => set("title", e.target.value)}
                placeholder="Ex: Como se tornar Social Media em 2025"
              />
            </div>

            {/* Ministrante */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>Ministrante</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "#99A6AB" }} />
                <Input
                  value={form.presenter}
                  onChange={e => set("presenter", e.target.value)}
                  placeholder="Ex: João Silva"
                  className="pl-8"
                />
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>Descrição</label>
              <Textarea
                value={form.description}
                onChange={e => set("description", e.target.value)}
                placeholder="Sobre o que será essa aula..."
                className="resize-none"
                rows={2}
              />
            </div>

            {/* Categoria */}
            <div>
              <label className="text-xs font-semibold block mb-2" style={{ color: "#003326" }}>Categoria</label>
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map(cat => {
                  const cc = CATEGORY_COLORS[cat];
                  const active = form.category === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => set("category", active ? "" : cat)}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                      style={active
                        ? { background: cc.bg, color: cc.text, outline: "2px solid #00DCA4", outlineOffset: "2px" }
                        : { background: "#F2F4F4", color: "#646C6F" }}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Máximo de participantes (aparece quando Oficina) */}
            {form.category === "Oficina" && (
              <div>
                <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>
                  Máximo de participantes
                  <span className="ml-1 font-normal" style={{ color: "#99A6AB" }}>(deixe vazio = sem limite)</span>
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "#99A6AB" }} />
                  <Input
                    type="number"
                    min="1"
                    value={form.max_participants}
                    onChange={e => set("max_participants", e.target.value)}
                    placeholder="Ex: 30"
                    className="pl-8"
                  />
                </div>
              </div>
            )}

            {/* Status */}
            <div>
              <label className="text-xs font-semibold block mb-2" style={{ color: "#003326" }}>Status</label>
              <div className="flex gap-2">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => set("live_status", key)}
                    className="flex-1 py-2.5 rounded-xl text-xs font-bold transition-all"
                    style={form.live_status === key
                      ? { background: cfg.bg, color: cfg.text, outline: "2px solid #00DCA4", outlineOffset: "2px" }
                      : { background: cfg.bg, color: cfg.text, opacity: 0.45 }}
                  >
                    {key === "live" && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle animate-pulse"
                        style={{ background: cfg.text }} />
                    )}
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Data e hora */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>Data e Hora da aula</label>
              <Input
                type="datetime-local"
                value={form.live_date}
                onChange={e => set("live_date", e.target.value)}
              />
            </div>

            {/* Link do player */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>
                Link do player (YouTube Live, Zoom, Meet...)
              </label>
              <div className="relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "#99A6AB" }} />
                <Input
                  value={form.live_url}
                  onChange={e => set("live_url", e.target.value)}
                  placeholder="https://youtube.com/live/..."
                  className="pl-8"
                />
              </div>
            </div>

            {/* Link da gravação */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>
                Link da gravação
                {form.live_status !== "recording" && (
                  <span className="ml-1 font-normal" style={{ color: "#99A6AB" }}>(preencher ao encerrar)</span>
                )}
              </label>
              <div className="relative">
                <Video className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "#99A6AB" }} />
                <Input
                  value={form.video_url}
                  onChange={e => set("video_url", e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="pl-8"
                  style={form.live_status === "recording" ? { borderColor: "#00DCA4" } : {}}
                />
              </div>
            </div>

            {/* Publicar toggle */}
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "#F2F4F4" }}>
              <button
                type="button"
                onClick={() => set("is_published", !form.is_published)}
                className="w-10 h-6 rounded-full transition-all flex-shrink-0 relative"
                style={{ background: form.is_published ? "#00DCA4" : "#D9DBDA" }}
              >
                <span
                  className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all"
                  style={{ left: form.is_published ? "calc(100% - 22px)" : "2px" }}
                />
              </button>
              <div>
                <p className="text-sm font-semibold" style={{ color: "#2E3233" }}>
                  {form.is_published ? "Visível na Home" : "Rascunho — oculta"}
                </p>
                <p className="text-xs" style={{ color: "#99A6AB" }}>
                  Quando publicado, aparece na seção "Encontros ao Vivo" da Home.
                </p>
              </div>
            </div>

            {/* Ações */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 py-3 rounded-full text-sm font-semibold"
                style={{ background: "#F2F4F4", color: "#646C6F" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!form.title || saving}
                className="flex-1 py-3 rounded-full text-sm font-bold transition-opacity"
                style={form.title && !saving
                  ? { background: "#00DCA4", color: "#003326" }
                  : { background: "#E5E9EA", color: "#99A6AB" }}
              >
                {saving ? "Salvando..." : editing ? "Salvar alterações" : "Criar aula"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
